import React, { useState, useRef, useEffect } from 'react';
import { Search, Scan, X, Loader2 } from 'lucide-react';

interface ScanInputProps {
  value: string;
  onChange: (value: string) => void;
  onScan?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  autoFocus?: boolean;
  className?: string;
  scanDelay?: number; // milliseconds to wait before triggering scan
}

export const ScanInput: React.FC<ScanInputProps> = ({
  value,
  onChange,
  onScan,
  placeholder = 'Skriv inn eller skann...',
  disabled = false,
  loading = false,
  autoFocus = false,
  className = '',
  scanDelay = 100
}) => {
  const [isScanMode, setIsScanMode] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const scanTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastInputTimeRef = useRef<number>(0);
  const scanBufferRef = useRef<string>('');

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  // Handle keyboard input for scanning detection
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (disabled || loading) return;
      
      const now = Date.now();
      const timeSinceLastInput = now - lastInputTimeRef.current;
      
      // If input is coming very fast (< 50ms between keystrokes), it's likely a scanner
      if (timeSinceLastInput < 50 && e.key !== 'Enter') {
        setIsScanMode(true);
        scanBufferRef.current += e.key;
        
        // Clear any existing timeout
        if (scanTimeoutRef.current) {
          clearTimeout(scanTimeoutRef.current);
        }
        
        // Set timeout to process scan after delay
        scanTimeoutRef.current = setTimeout(() => {
          if (scanBufferRef.current.length > 3) { // Minimum length for valid scan
            onChange(scanBufferRef.current);
            if (onScan) {
              onScan(scanBufferRef.current);
            }
          }
          scanBufferRef.current = '';
          setIsScanMode(false);
        }, scanDelay);
      } else {
        // Normal typing - reset scan mode
        setIsScanMode(false);
        scanBufferRef.current = '';
        if (scanTimeoutRef.current) {
          clearTimeout(scanTimeoutRef.current);
        }
      }
      
      lastInputTimeRef.current = now;
    };

    // Only attach to document if this input is focused
    if (document.activeElement === inputRef.current) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (scanTimeoutRef.current) {
        clearTimeout(scanTimeoutRef.current);
      }
    };
  }, [disabled, loading, onChange, onScan, scanDelay]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    
    // Reset scan mode if user is typing normally
    if (!isScanMode) {
      scanBufferRef.current = '';
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && value.trim()) {
      e.preventDefault();
      if (onScan) {
        onScan(value.trim());
      }
    }
  };

  const handleClear = () => {
    onChange('');
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  return (
    <div className={`relative flex items-center ${className}`}>
      {/* Scan mode indicator */}
      {isScanMode && (
        <div className="absolute left-2 top-1/2 transform -translate-y-1/2 z-10">
          <Scan className="h-4 w-4 text-green-500 animate-pulse" />
        </div>
      )}
      
      {/* Search icon when not in scan mode */}
      {!isScanMode && !loading && (
        <div className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10">
          <Search className="h-4 w-4 text-gray-400" />
        </div>
      )}
      
      {/* Loading spinner */}
      {loading && (
        <div className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10">
          <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />
        </div>
      )}
      
      {/* Input field */}
      <input
        ref={inputRef}
        type="text"
        enterKeyHint="send"
        autoComplete="off"
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck={false}
        value={value}
        onChange={handleInputChange}
        onKeyPress={handleKeyPress}
        placeholder={placeholder}
        disabled={disabled || loading}
        className={`
          w-full py-2 pl-10 pr-10
          border border-gray-300 rounded-lg
          focus:ring-2 focus:ring-blue-500 focus:border-transparent
          disabled:opacity-50 disabled:cursor-not-allowed
          ${isScanMode ? 'border-green-500 bg-green-50' : ''}
          ${loading ? 'cursor-wait' : ''}
        `}
      />
      
      {/* Clear button */}
      {value && !loading && (
        <button
          onClick={handleClear}
          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
          type="button"
        >
          <X className="h-4 w-4" />
        </button>
      )}
      
      {/* Scan mode indicator text */}
      {isScanMode && (
        <div className="absolute top-full left-0 mt-1 text-xs text-green-600 font-medium">
          Skanning pågår...
        </div>
      )}
    </div>
  );
};
